import React, { FC, useCallback, useEffect, useMemo } from 'react';

import { useQuery } from '@apollo/client';
import { Box, Collapse, Tabs, Typography } from '@mui/material';
import { styled } from '@mui/material';
import Tab from '@mui/material/Tab';
import { Theme } from '@mui/material/styles';

import AnimatedSkeleton from '~/components/AnimatedSkeleton';
import CommentMessage, { RatingType } from '~/components/CommentMessage/CommentMessage';
import Pagination, { usePagination } from '~/components/Pagination/Pagination';
import Disclaimer from '~/components/Summary/Disclaimer';
import Summary from '~/components/Summary/Summary';
import TabPanel, { a11yProps } from '~/components/TabPanel/TabPanel';
import { useWithoutNavigation } from '~/context/OnlineContentConnectorContext';
import { GET_LIST_REVIEWS_FOR_ENTITY } from '~/graphql/videos';
import useIsSsku from '~/hooks/tenant/useIsSsku';
import useAuth from '~/hooks/useAuth';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import CommentForm from '~/pages/SingleVideoPage/components/CommentForm';
import CommentReviewCard from '~/pages/SingleVideoPage/components/CommentReviewCard';
import Expert from '~/pages/SingleVideoPage/components/Expert/Expert';
import Overview from '~/pages/SingleVideoPage/components/Overview/Overview';
import VideoAccordion from '~/pages/SingleVideoPage/components/VideoAccordion/VideoAccordion';
import { TabsEnum } from '~/pages/SingleVideoPage/const';
import { IUseVideoReview } from '~/pages/SingleVideoPage/hooks/useVideoReview';
import { IVideoWithUrl } from '~/pages/SingleVideoPage/types';
import { MIDDLE_MARGIN, MIDDLE_MARGIN_PX, SKELETON_TEXT_HEIGHT_PX, useIsWidthUp } from '~/theme';
import {
  Query,
  QueryGetListReviewsForEntityArgs,
  ReviewableType,
  ReviewStatus,
  Video,
} from '~/types';
import updateReview from '~/utils/updateReview';

interface VideoTabsProps {
  video?: Video | null;
  isXApiPage?: boolean;
  loading: boolean;
  videosWithUrl: IVideoWithUrl[];
  tabs: any;
  overviewExists: boolean;
  summaryExists: boolean;
  tabIndex: number;
  setTabIndex: (value: ((prevState: number) => number) | number) => void;
  videoReview: IUseVideoReview;
}

const PAGE_STEP = 10;

const VideoTabs: FC<VideoTabsProps> = ({
  video,
  isXApiPage = false,
  videosWithUrl,
  loading,
  tabs,
  overviewExists,
  summaryExists,
  tabIndex,
  setTabIndex,
  videoReview,
}) => {
  const { t } = useTenantTranslation();
  const { isAuthenticated } = useAuth();
  const isMobile = !useIsWidthUp('md');
  const withoutNavigation = useWithoutNavigation();
  const isSsku = useIsSsku();
  const { currentItem, handlePrev, handleNext, setTotalItems, currentPage } =
    usePagination(PAGE_STEP);

  const showRating = isAuthenticated && !withoutNavigation;
  const {
    leaveReview,
    likedVideo,
    likedOutsideCommentSection,
    status: reviewStatus,
    refetchLearnerReview,
    loadingReviewSubmit,
  } = videoReview;

  const { data: reviewsList } = useQuery<
    Pick<Query, 'getListReviewsForEntity'>,
    QueryGetListReviewsForEntityArgs
  >(GET_LIST_REVIEWS_FOR_ENTITY, {
    variables: {
      entity_type: ReviewableType.Video,
      entity_id: Number(video?.id),
      pagination_option: {
        page: currentPage,
        count_items_on_page: PAGE_STEP,
      },
      include_test_publish_status: true,
    },
    skip: !video?.id || !isAuthenticated || isXApiPage,
  });

  const totalReviews = reviewsList?.getListReviewsForEntity?.count_reviews || 0;

  useEffect(() => {
    if (totalReviews) {
      setTotalItems(totalReviews);
    }
  }, [setTotalItems, totalReviews]);

  const updatedReviews = useMemo(
    () => reviewsList?.getListReviewsForEntity?.reviews?.map(updateReview),
    [reviewsList],
  );

  const handleChangeIndex = useCallback(
    (event, newValue) => {
      setTabIndex(newValue);
    },
    [setTabIndex],
  );

  const handleChangeAccordion = useCallback(
    (panel) => (event, isExpanded) => {
      setTabIndex(isExpanded ? panel : -1);
    },
    [setTabIndex],
  );

  const leaveReviewWithComment = async (comment: string) => {
    if (likedVideo !== undefined) {
      await leaveReview({ liked: likedVideo, comment });
      refetchLearnerReview();
    }
  };

  if (loading) {
    return (
      <Wrapper data-testid="videoTabs">
        <LoadingTabs xs={SKELETON_TEXT_HEIGHT_PX} />
        <Overview loading={loading} />
      </Wrapper>
    );
  }

  return (
    <Wrapper data-testid="videoTabs">
      {isMobile ? (
        <>
          {overviewExists && (
            <VideoAccordion
              expanded={tabIndex === tabs[TabsEnum.Overview]}
              onChange={handleChangeAccordion(tabs[TabsEnum.Overview])}
            >
              <MobileTab>{t('common.objectives')}</MobileTab>
              <Overview
                keyLearningObjectives={video?.key_learning_objectives}
                summary={video?.executive_summary}
                skills={video?.skills}
                impacts={video?.impacts}
              >
                <Disclaimer />
              </Overview>
            </VideoAccordion>
          )}
          {summaryExists && (
            <VideoAccordion
              expanded={tabIndex === tabs[TabsEnum.Summary]}
              onChange={handleChangeAccordion(tabs[TabsEnum.Summary])}
            >
              <MobileTab id="expertSection">{t('common.summary')}</MobileTab>
              <Summary summary={video?.summary}>
                <Disclaimer />
              </Summary>
            </VideoAccordion>
          )}
          <VideoAccordion
            expanded={tabIndex === tabs[TabsEnum.Expert]}
            onChange={handleChangeAccordion(tabs[TabsEnum.Expert])}
          >
            <MobileTab id="expertSection">{t('common.expert')}</MobileTab>
            <Expert
              loading={loading}
              expert={video?.user}
              videos={videosWithUrl}
              restrictNavigation={isXApiPage || withoutNavigation}
            />
          </VideoAccordion>
          {showRating && (
            <VideoAccordion
              expanded={tabIndex === tabs[TabsEnum.Reviews]}
              onChange={handleChangeAccordion(tabs[TabsEnum.Reviews])}
            >
              <MobileTab>{t(isSsku ? 'common.reviews' : 'page.video.comments')}</MobileTab>
              <Col>
                <Collapse in={!reviewStatus || reviewStatus === ReviewStatus.Rejected}>
                  <CommentForm
                    title={video?.title}
                    leaveReview={leaveReview}
                    likedVideo={likedVideo}
                    likedOutsideCommentSection={likedOutsideCommentSection}
                    leaveReviewWithComment={leaveReviewWithComment}
                    loading={loadingReviewSubmit}
                  />
                </Collapse>
                <Collapse in={reviewStatus === ReviewStatus.PendingApproval}>
                  <CommentReviewCard />
                </Collapse>
                <div>
                  {updatedReviews?.map((review, index) => (
                    <CommentMessage
                      key={index}
                      rating={review?.stars_rating}
                      ratingType={RatingType.Thumb}
                      date={review?.created_at}
                      name={review?.full_name}
                      text={review?.comment}
                      learnerId={review?.learner?.id}
                    />
                  ))}
                </div>
                {Boolean(totalReviews > 0) && (
                  <>
                    <Pagination
                      current={currentItem}
                      total={totalReviews}
                      step={PAGE_STEP}
                      handlePrev={handlePrev}
                      handleNext={handleNext}
                    />
                    <Divider />
                  </>
                )}
              </Col>
            </VideoAccordion>
          )}
        </>
      ) : (
        <>
          <StyledTabs
            value={tabIndex}
            onChange={handleChangeIndex}
            textColor="inherit"
            aria-label="video info tabs"
          >
            {overviewExists && (
              <Tab
                label={t('common.objectives')}
                {...a11yProps(tabs[TabsEnum.Overview])}
                data-testid="overviewTab"
              />
            )}
            {summaryExists && (
              <Tab
                label={t('common.summary')}
                {...a11yProps(tabs[TabsEnum.Summary])}
                data-testid="summaryTab"
              />
            )}
            <Tab
              label={t('common.expert')}
              {...a11yProps(tabs[TabsEnum.Expert])}
              data-testid="expertTab"
            />
            {showRating && (
              <Tab
                label={t(isSsku ? 'common.reviews' : 'page.video.comments')}
                {...a11yProps(tabs[TabsEnum.Reviews])}
                data-testid="ratingTab"
              />
            )}
          </StyledTabs>
          {overviewExists && (
            <TabPanel value={tabIndex} index={tabs[TabsEnum.Overview]}>
              <Overview
                keyLearningObjectives={video?.key_learning_objectives}
                summary={video?.executive_summary}
                skills={video?.skills}
                impacts={video?.impacts}
              >
                <Disclaimer />
              </Overview>
            </TabPanel>
          )}
          {summaryExists && (
            <TabPanel value={tabIndex} index={tabs[TabsEnum.Summary]}>
              <Summary summary={video?.summary}>
                <Disclaimer />
              </Summary>
            </TabPanel>
          )}
          {showRating && (
            <TabPanel value={tabIndex} index={tabs[TabsEnum.Reviews]} data-testid="reviewTab">
              <Collapse in={!reviewStatus || reviewStatus === ReviewStatus.Rejected}>
                <CommentForm
                  title={video?.title}
                  leaveReview={leaveReview}
                  likedVideo={likedVideo}
                  likedOutsideCommentSection={likedOutsideCommentSection}
                  leaveReviewWithComment={leaveReviewWithComment}
                  loading={loadingReviewSubmit}
                />
              </Collapse>
              <Collapse in={reviewStatus === ReviewStatus.PendingApproval}>
                <CommentReviewCard />
              </Collapse>
              <div>
                {updatedReviews?.map((review, index) => (
                  <CommentMessage
                    key={index}
                    rating={review?.stars_rating}
                    ratingType={RatingType.Thumb}
                    date={review?.created_at}
                    name={review?.full_name}
                    text={review?.comment}
                    learnerId={review?.learner?.id}
                  />
                ))}
              </div>
              {Boolean(totalReviews > 0) && (
                <>
                  <Pagination
                    current={currentItem}
                    total={totalReviews}
                    step={PAGE_STEP}
                    handlePrev={handlePrev}
                    handleNext={handleNext}
                  />
                  <Divider />
                </>
              )}
            </TabPanel>
          )}
          <TabPanel value={tabIndex} index={tabs[TabsEnum.Expert]}>
            <Expert
              loading={loading}
              expert={video?.user}
              videos={videosWithUrl}
              restrictNavigation={isXApiPage || withoutNavigation}
            />
          </TabPanel>
        </>
      )}
    </Wrapper>
  );
};

export default VideoTabs;

const Col = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
});
const Divider = styled('div')(({ theme }: { theme: Theme }) => ({
  height: 1,
  width: '100%',
  marginTop: MIDDLE_MARGIN,
  background: theme.palette.common.violetAnalyticsCard,
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));
const LoadingTabs = styled(AnimatedSkeleton)`
  margin-bottom: ${MIDDLE_MARGIN_PX};
`;
const Wrapper = styled(Box)`
  flex: 1;
  min-width: 290px;
`;
const MobileTab = styled(Typography)`
  font-size: 18px;
  line-height: 1;
  font-weight: 700;
`;
const StyledTabs = styled(Tabs)`
  margin-bottom: ${MIDDLE_MARGIN_PX};
`;
